import React from 'react';
import { Divider, Select } from 'antd';
import { useFormikContext } from 'formik';
import _get from 'lodash/get';

import { useCityList, useStateList } from 'api/mappings';
import { TENANTS } from 'appConstants';
import { InputLimitWidth } from 'components/Layout/Form';
import { FlexContainer } from 'components/Layout/common';
import Label from 'components/Text/Label';
import { TRuleCreatePage } from 'containers/Rule/i18n/TModules';
import { useTenant } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import CustomFilterField from './CustomFilterField';

interface LocationProps {
  customFiltersData: any[];
}

const Location = ({ customFiltersData }: LocationProps) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const tenantConfig = useTenant();

  const { t } = useMultiLanguageTranslation();

  const { data: cities = [] } = useCityList();
  const { data: states = [] } = useStateList();

  if (
    tenantConfig.name === TENANTS.TRANSCYCLE ||
    tenantConfig.name === TENANTS.TRANSCYCLE_PREPROD
  ) {
    return (
      <>
        {customFiltersData?.map(c => (
          <CustomFilterField key={c.key} fieldMapping={c} />
        ))}
      </>
    );
  }

  return (
    <>
      <div className="spaced">
        <Label dark bold>
          {t(`${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.LocationFilter.SelectCity`)}
        </Label>
      </div>
      <FlexContainer>
        <div>
          <InputLimitWidth>
            <Select
              value={_get(values, 'filters.city[0].criteria[0].policy')}
              onSelect={value => {
                setFieldValue('filters.city[0].criteria[0].policy', value);
              }}
              placeholder={t(
                `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.LocationFilter.Includes`
              )}
              data-testid="cities-include-select"
            >
              <Select.Option value="include" data-testid="cities-include-select-include">
                {t(`${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.LocationFilter.Includes`)}
              </Select.Option>
              <Select.Option value="exclude" data-testid="cities-include-select-exclude">
                {t(`${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.LocationFilter.Excludes`)}
              </Select.Option>
            </Select>
          </InputLimitWidth>
        </div>
        <div style={{ flex: '1 1 auto' }}>
          <Select
            value={_get(values, 'filters.city[0].criteria[0].values')}
            mode="multiple"
            showArrow={true}
            allowClear
            placeholder={t(
              `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.LocationFilter.SelectCityPlaceholder`
            )}
            onChange={values => {
              setFieldValue('filters.city[0].criteria[0].values', values);
            }}
            filterOption={(inputValue, option) =>
              (option.props.children as string).toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
            }
          >
            {cities.map(city => (
              <Select.Option key={city.id} value={city.id}>
                {city.name}
              </Select.Option>
            ))}
          </Select>
        </div>
      </FlexContainer>
      <Divider />
      <div className="spaced">
        <Label dark bold>
          {t(`${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.LocationFilter.SelectState`)}
        </Label>
      </div>
      <FlexContainer>
        <div>
          <InputLimitWidth>
            <Select
              value={_get(values, 'filters.states[0].criteria[0].policy')}
              onSelect={value => {
                setFieldValue('filters.states[0].criteria[0].policy', value);
              }}
              placeholder={t(
                `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.LocationFilter.Includes`
              )}
              data-testid="states-include-select"
            >
              <Select.Option value="include" data-testid="states-include-select-include">
                {t(`${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.LocationFilter.Includes`)}
              </Select.Option>
              <Select.Option value="exclude" data-testid="states-include-select-exclude">
                {t(`${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.LocationFilter.Excludes`)}
              </Select.Option>
            </Select>
          </InputLimitWidth>
        </div>
        <div style={{ flex: '1 1 auto' }}>
          <Select
            value={_get(values, 'filters.states[0].criteria[0].values')}
            mode="multiple"
            showArrow={true}
            placeholder={t(
              `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.LocationFilter.SelectStatePlaceholder`
            )}
            allowClear
            onChange={value => {
              setFieldValue('filters.states[0].criteria[0].values', value);
            }}
            filterOption={(inputValue, option) =>
              (option.props.children as string).toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
            }
          >
            {states.map(state => (
              <Select.Option key={state.id} value={state.id}>
                {state.name}
              </Select.Option>
            ))}
          </Select>
        </div>
      </FlexContainer>
    </>
  );
};

export default Location;
